import { useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { SchoolYearStatus } from '@dataconnect/generated';
import {
  useListSchoolYearsBySchool,
  useListSpecialDaysBySchoolYear,
  useListDailySchedulesBySchoolYear,
  useListScheduleAssignmentsBySchoolYear,
} from '@dataconnect/generated/react';

import { type TeacherClass, defaultSchedulePeriod } from 'src/class/class-provider';

import { parseMinutes } from 'src/sections/curriculum/pacing/use-pacing';

// ----------------------------------------------------------------------
// View-model for the teacher weekly grid. Given the active school's classes and a
// week (Monday), it resolves — per weekday — the applicable daily schedule and which
// classes meet, honouring holidays (non-instructional special days) and per-date
// alternate-schedule assignments (e.g. early release).
//
// There is no stored day-of-week: a class meets every instructional weekday on the
// schedules it has a `ClassSchedulePeriod` for. So the grid is computed per concrete
// date, exactly like `listClassMeetingDates` in packages/ui, but for one week at a
// time (no need to enumerate the whole school year). The calendar backdrop (special
// days, assignments, daily schedules) is school-year-scoped, so it loads once — not
// once per class.
//
// `useTeacherDays` is the same resolution over any date range (the Calendar page, epic
// #534); `useTeacherWeek` is that range for one Mon–Fri week.
// ----------------------------------------------------------------------

const ISO = 'YYYY-MM-DD';
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** A bell-schedule slot as rendered in a cell. */
export type WeekPeriod = { name: string; startTime: string; endTime: string };

/** One class meeting on a given day, placed in the row of its anchor period. */
export type DayMeeting = { cls: TeacherClass; period: WeekPeriod; rowKey: string };

export type WeekDay = {
  iso: string;
  weekdayLabel: string;
  dayOfMonth: number;
  isToday: boolean;
  /** The date falls outside the school year's start–end range (e.g. summer break). */
  isOutOfSession: boolean;
  /** A non-instructional special day covers this date. */
  isHoliday: boolean;
  holidayName: string | null;
  /** An instructional special day covering this date (e.g. "Early Release", "Conferences"), else null. */
  specialDayName: string | null;
  /** Name of the daily schedule that applies this date. */
  scheduleName: string | null;
  /** Whether the applied schedule is the school's default (else an alternate, e.g. early release). */
  isDefaultSchedule: boolean;
  /** Whether lessons can be placed on this day's schedule (epic #277; false on e.g. Half Days). */
  lessonsAllowed: boolean;
  meetings: DayMeeting[];
};

/** A period row in the grid gutter. Keyed by a class's anchor (default) start time. */
export type WeekRow = { key: string; label: string; time: string; startMinutes: number };

export type UseTeacherWeekResult = {
  isPending: boolean;
  /** The school year the grid is about (the school's ACTIVE year), or null. */
  schoolYearId: string | null;
  /** The resolved school year's name + start/end range, for out-of-session messaging. */
  schoolYear: { name: string; startDate: string; endDate: string } | null;
  /** Classes actually placed on the grid (those in `schoolYearId`). */
  classes: TeacherClass[];
  rows: WeekRow[];
  days: WeekDay[];
};

/** The Monday of the week containing `d` (weeks run Mon–Fri; Sun rolls back to the prior Mon). */
export function startOfWeek(d: Dayjs = dayjs()): Dayjs {
  const dow = d.day(); // 0 = Sun … 6 = Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  return d.add(diff, 'day').startOf('day');
}

/**
 * The period a class anchors to in the grid: its period on the default daily schedule,
 * falling back to its earliest period if it only meets on alternate schedules. Null if
 * the class has no scheduled period at all (it won't appear on the grid).
 */
function anchorPeriod(c: TeacherClass): WeekPeriod | null {
  const def = defaultSchedulePeriod(c);
  if (def) return def;
  let best: WeekPeriod | null = null;
  for (const sp of c.schedulePeriods ?? []) {
    const p = sp.schedulePeriod;
    if (!best || (parseMinutes(p.startTime) ?? Infinity) < (parseMinutes(best.startTime) ?? Infinity)) {
      best = p;
    }
  }
  return best;
}

/** Every weekday (Mon–Fri) from `from` to `to`, inclusive. */
export function weekdaysBetween(from: Dayjs, to: Dayjs): Dayjs[] {
  const out: Dayjs[] = [];
  for (let d = from.startOf('day'); !d.isAfter(to, 'day'); d = d.add(1, 'day')) {
    if (d.day() !== 0 && d.day() !== 6) out.push(d);
  }
  return out;
}

export function useTeacherWeek(classesAll: TeacherClass[], weekStart: Dayjs): UseTeacherWeekResult {
  return useTeacherDays(classesAll, weekStart, weekStart.add(4, 'day'));
}

/** `useTeacherWeek` over any date range: one resolved `WeekDay` per weekday from `from` to `to`. */
export function useTeacherDays(classesAll: TeacherClass[], from: Dayjs, to: Dayjs): UseTeacherWeekResult {
  // The grid is about a single school year — the school's ACTIVE year (same default as
  // the Calendar view), so "this week" always reflects the year currently in session.
  // The classes come pre-scoped to the active school, so every school year they touch
  // belongs to that school; we just need to know which one is ACTIVE.
  const schoolId = classesAll[0]?.course.schoolYear.school.id ?? '';
  const yearsQ = useListSchoolYearsBySchool({ schoolId }, { enabled: !!schoolId });

  const schoolYearId = useMemo(() => {
    const classYearIds = new Set(classesAll.map((c) => c.course.schoolYear.id));
    const years = yearsQ.data?.schoolYears ?? [];

    // Prefer the ACTIVE year when the teacher actually has classes in it; otherwise fall
    // back to the year most of their classes are in, so the grid never goes empty for a
    // teacher whose classes live outside the ACTIVE year.
    const active = years.find((y) => y.status === SchoolYearStatus.ACTIVE);
    if (active && classYearIds.has(active.id)) return active.id;

    const counts = new Map<string, number>();
    for (const c of classesAll) {
      const id = c.course.schoolYear.id;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    let best: string | null = null;
    let bestN = 0;
    for (const [id, n] of counts) {
      if (n > bestN) {
        best = id;
        bestN = n;
      }
    }
    return best;
  }, [classesAll, yearsQ.data]);

  const classes = useMemo(
    () => classesAll.filter((c) => c.course.schoolYear.id === schoolYearId),
    [classesAll, schoolYearId]
  );

  const schoolYear = useMemo(() => {
    const y = (yearsQ.data?.schoolYears ?? []).find((yy) => yy.id === schoolYearId);
    return y ? { name: y.name, startDate: y.startDate, endDate: y.endDate } : null;
  }, [yearsQ.data, schoolYearId]);

  const enabled = !!schoolYearId;
  const yearArg = { schoolYearId: schoolYearId ?? '' };
  const specialDaysQ = useListSpecialDaysBySchoolYear(yearArg, { enabled });
  const schedulesQ = useListDailySchedulesBySchoolYear(yearArg, { enabled });
  const assignmentsQ = useListScheduleAssignmentsBySchoolYear(yearArg, { enabled });

  const isPending =
    (!!schoolId && yearsQ.isPending) ||
    (enabled && (specialDaysQ.isPending || schedulesQ.isPending || assignmentsQ.isPending));

  // Period rows — one per distinct anchor start time across the placed classes.
  const rows = useMemo<WeekRow[]>(() => {
    const map = new Map<string, WeekRow>();
    for (const c of classes) {
      const p = anchorPeriod(c);
      if (!p) continue;
      const mins = parseMinutes(p.startTime);
      if (mins == null) continue;
      if (!map.has(p.startTime)) {
        map.set(p.startTime, { key: p.startTime, label: p.name, time: p.startTime, startMinutes: mins });
      }
    }
    return [...map.values()].sort((a, b) => a.startMinutes - b.startMinutes);
  }, [classes]);

  // Depend on the dates, not the Dayjs objects (callers often build them each render).
  const fromIso = from.format(ISO);
  const toIso = to.format(ISO);
  const days = useMemo<WeekDay[]>(() => {
    const specialDays = specialDaysQ.data?.specialDays ?? [];
    const schedules = schedulesQ.data?.dailySchedules ?? [];
    const assignments = assignmentsQ.data?.scheduleAssignments ?? [];

    const defaultSchedule = schedules.find((s) => s.isDefault) ?? null;
    const assignmentByDate = new Map(assignments.map((a) => [a.date, a.dailySchedule]));
    const todayIso = dayjs().format(ISO);

    return weekdaysBetween(dayjs(fromIso), dayjs(toIso)).map((d) => {
      const iso = d.format(ISO);
      const weekdayLabel = WEEKDAY_LABELS[d.day()];

      // Outside the school year (e.g. summer break): nothing is in session, so no
      // schedule applies and no class meets — regardless of the default bell schedule.
      const isOutOfSession =
        !!schoolYear && (iso < schoolYear.startDate || iso > schoolYear.endDate);

      // Holiday: any non-instructional special day whose range covers this date.
      const holiday =
        !isOutOfSession &&
        specialDays.find((sd) => sd.nonInstructional && iso >= sd.startDate && iso <= sd.endDate);

      const special =
        !isOutOfSession && !holiday
          ? specialDays.find((sd) => !sd.nonInstructional && iso >= sd.startDate && iso <= sd.endDate)
          : undefined;

      const assigned = assignmentByDate.get(iso) ?? null; // { id, name } | null
      const resolvedId = assigned?.id ?? defaultSchedule?.id ?? null;
      const scheduleName = isOutOfSession ? null : (assigned?.name ?? defaultSchedule?.name ?? null);
      const isDefaultSchedule =
        !isOutOfSession && resolvedId != null && resolvedId === defaultSchedule?.id;
      const lessonsAllowed = schedules.find((s) => s.id === resolvedId)?.allowsLessons ?? true;

      const meetings: DayMeeting[] = [];
      if (!isOutOfSession && !holiday && resolvedId) {
        for (const c of classes) {
          const anchor = anchorPeriod(c);
          if (!anchor) continue;
          const sp = c.schedulePeriods?.find((s) => s.dailySchedule.id === resolvedId);
          if (!sp) continue; // class has no period on the schedule that applies this date
          meetings.push({
            cls: c,
            period: {
              name: sp.schedulePeriod.name,
              startTime: sp.schedulePeriod.startTime,
              endTime: sp.schedulePeriod.endTime,
            },
            rowKey: anchor.startTime,
          });
        }
      }

      return {
        iso,
        weekdayLabel,
        dayOfMonth: d.date(),
        isToday: iso === todayIso,
        isOutOfSession,
        isHoliday: !!holiday,
        holidayName: holiday ? holiday.name : null,
        specialDayName: special ? special.name : null,
        scheduleName,
        isDefaultSchedule,
        lessonsAllowed,
        meetings,
      };
    });
  }, [classes, fromIso, toIso, schoolYear, specialDaysQ.data, schedulesQ.data, assignmentsQ.data]);

  return { isPending: !!isPending, schoolYearId, schoolYear, classes, rows, days };
}
